// ... existing imports ...
import { formatPercent, formatThousands, isNullOrUndefined } from '.';

interface ROIValues {
  projectedReturnOnInvestment: number;
  projectedCompoundAnnualGrowthRate: number;
  projectedInternalRateOfReturn: number | undefined;
  investmentPeriodYears: number;
  isIrrSuspect?: boolean;
}

export const IRR_SUSPECT_MESSAGE = 'Not calculable';
export const IRR_SUSPECT_EXPLANATION =
  'IRR cannot be reliably determined for products where payments overlap with benefit payouts. Use Total ROI or Annual ROI instead.';
export const IRR_SUSPECT_EXPLANATION_COMBINED =
  "A combined IRR can't be reliably calculated for this client. When multiple funding sources are added together, payments from one source can overlap with payouts from another, and the combined cash flow timing has more than one valid mathematical answer. Refer to each funding source's individual IRR above, or use Total ROI or CAGR instead.";

export function formatROI(
  values: ROIValues,
  calculationType: ReturnOnInvestmentCalculationType | undefined,
): string {
  const formatPercentToTwoDecimals = (value: number) => formatPercent(value, 2);
  const formatYears = (years: number) =>
    `${formatThousands(years, '0', 1)} ${years === 1 ? 'yr' : 'yrs'}`;

  switch (calculationType) {
    case 'compoundAnnualGrowthRate':
      if (values.investmentPeriodYears <= 0) return 'N/A';
      if (!isFinite(values.projectedCompoundAnnualGrowthRate)) return 'N/A';
      return `${formatPercentToTwoDecimals(values.projectedCompoundAnnualGrowthRate)} (${formatYears(values.investmentPeriodYears)})`;
    case 'internalRateOfReturn':
      if (isNullOrUndefined(values.projectedInternalRateOfReturn) || values.isIrrSuspect) {
        return IRR_SUSPECT_MESSAGE;
      }
      if (!isFinite(values.projectedInternalRateOfReturn)) return 'N/A';
      return `${formatPercentToTwoDecimals(values.projectedInternalRateOfReturn)} (${formatYears(values.investmentPeriodYears)})`;
    case 'total':
    default:
      if (!isFinite(values.projectedReturnOnInvestment)) return 'N/A';
      return formatPercentToTwoDecimals(values.projectedReturnOnInvestment);
  }
}

export function getROILabel(
  calculationType: ReturnOnInvestmentCalculationType | undefined,
): string {
  switch (calculationType) {
    case 'internalRateOfReturn':
      return 'Annual IRR';
    case 'compoundAnnualGrowthRate':
      return 'Annual ROI';
    case 'total':
    default:
      return 'Total ROI';
  }
}
